import { ArrowRight, BookOpen, FileText, Wrench } from "lucide-react";
import { Link } from "wouter";
import { trackSectionCtaClick } from "@/lib/analytics";

type ResourceKind = "tool" | "guide" | "audit";

type Resource = {
  href: string;
  eyebrow: string;
  title: string;
  description: string;
  kind: ResourceKind;
};

export type RelatedToolsGuidesContext = "local" | "visibility" | "growth";

const RESOURCES: Record<RelatedToolsGuidesContext, Resource[]> = {
  local: [
    {
      href: "/free-ai-growth-audit/",
      eyebrow: "Free audit",
      title: "Check my AI visibility",
      description: "Start with a no-cost review of your local search, entity and schema signals.",
      kind: "audit",
    },
    {
      href: "/tools/utm-builder/",
      eyebrow: "Free tool",
      title: "Build trackable local campaign URLs",
      description: "Create consistent UTM links for Google Business Profile, social and local advertising activity.",
      kind: "tool",
    },
    {
      href: "/aeo/",
      eyebrow: "Guide hub",
      title: "Explore AI search foundations",
      description: "Use answer-first content, structured data and source evidence to make a business easier to understand.",
      kind: "guide",
    },
  ],
  visibility: [
    {
      href: "/free-ai-growth-audit/",
      eyebrow: "Free audit",
      title: "Benchmark your starting point",
      description: "Map technical, content and local-discovery gaps before investing in a growth programme.",
      kind: "audit",
    },
    {
      href: "/google-ads-optimization-ppc-performance/",
      eyebrow: "Practical guide",
      title: "Improve Google Ads measurement",
      description: "Use a seven-step diagnostic for conversion tracking, search intent and landing-page relevance.",
      kind: "guide",
    },
    {
      href: "/tools/utm-builder/",
      eyebrow: "Free tool",
      title: "Keep campaign attribution clean",
      description: "Generate consistent tags before testing radius-targeted or area-specific campaigns.",
      kind: "tool",
    },
  ],
  growth: [
    {
      href: "/google-ads-optimization-ppc-performance/",
      eyebrow: "Practical guide",
      title: "Improve paid search performance",
      description: "Work through measurement, intent, creative and landing-page checks before scaling spend.",
      kind: "guide",
    },
    {
      href: "/b2b-lead-generation-strategies-best/",
      eyebrow: "Growth guide",
      title: "Build a stronger lead system",
      description: "Follow a practical sequence from ICP definition through qualification and pipeline measurement.",
      kind: "guide",
    },
    {
      href: "/tools/ai-prompt-improver/",
      eyebrow: "Free tool",
      title: "Structure better marketing prompts",
      description: "Turn a rough content or campaign brief into a clearer, more reusable AI prompt.",
      kind: "tool",
    },
  ],
};

function ResourceIcon({ kind }: { kind: ResourceKind }) {
  if (kind === "tool") return <Wrench className="h-4 w-4" aria-hidden="true" />;
  if (kind === "audit") return <FileText className="h-4 w-4" aria-hidden="true" />;
  return <BookOpen className="h-4 w-4" aria-hidden="true" />;
}

export default function RelatedToolsAndGuides({
  context,
  className = "",
}: {
  context: RelatedToolsGuidesContext;
  className?: string;
}) {
  const resources = RESOURCES[context];

  return (
    <section
      className={`border-t border-white/10 py-14 ${className}`.trim()}
      aria-labelledby={`related-tools-guides-${context}`}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#00D4AA]">
        Practical next steps
      </p>
      <h2 id={`related-tools-guides-${context}`} className="font-serif text-2xl text-white sm:text-3xl">
        Related tools &amp; guides
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/60">
        Choose a focused resource to diagnose the opportunity, make activity measurable, or plan the next step in your local growth programme.
      </p>

      <div className="mt-7 grid gap-4 md:grid-cols-3">
        {resources.map((resource) => (
          <Link
            key={resource.href}
            href={resource.href}
            onClick={() =>
              trackSectionCtaClick({
                section_name: `related_tools_guides_${context}`,
                cta_text: resource.title,
                destination: resource.href,
              })
            }
            className="group rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-[#00D4AA]/45 hover:bg-white/[0.06]"
          >
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#00D4AA]">
              <ResourceIcon kind={resource.kind} />
              {resource.eyebrow}
            </span>
            <h3 className="mt-3 font-serif text-lg text-white transition-colors group-hover:text-teal-100">
              {resource.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{resource.description}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#00D4AA]">
              Explore resource <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
